/**
 * Trust Centre — chapter "SECURITY & PRIVACY" of the Trust & Support ask.
 *
 * IMPORTANT: language used here is deliberately conservative — we do NOT claim any
 * certification Suntronix does not hold. Wording follows the "Designed to support",
 * "Architecture supports", "Assessment available" pattern requested.
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, Database, KeyRound, RefreshCw, Server, FileText, Globe2, ClipboardCheck, ExternalLink, ArrowLeft } from "lucide-react";

const SECTIONS = [
  { icon: ShieldCheck, title: "Security Overview",
    body: "SUNTRONIX ONE is designed around defence-in-depth principles: identity, application and data layers each carry their own controls. Every request is authenticated, authorised, tenant-scoped and audited. We continuously invest in threat modelling and secure-by-default engineering." },
  { icon: Lock, title: "Encryption",
    body: "Data in transit is protected using TLS 1.2+. Data at rest in the MongoDB cluster is encrypted using AES-256. Application secrets are held outside the code base and rotated on a schedule." },
  { icon: KeyRound, title: "Access Controls",
    body: "SUNTRONIX ONE implements role-based access control across 20+ roles. Every list, detail and mutation endpoint enforces tenant isolation server-side. Administrators can manage users, roles and password resets from the built-in Administration area." },
  { icon: Database, title: "Data Protection",
    body: "Personal data related to employees is stored strictly for the operational purposes agreed with each customer. Access is limited on a need-to-know basis and every read/write against sensitive records is audited." },
  { icon: RefreshCw, title: "Backups & Continuity",
    body: "The platform is deployed with automated point-in-time backups. Recovery objectives are defined per tenant contract. Failover architecture supports multi-zone resilience." },
  { icon: FileText, title: "Data Retention",
    body: "Records are retained for the duration of the customer contract plus any statutory obligation. Customers may request configurable retention policies (long-term retention, legal hold) on request." },
  { icon: Server, title: "Vulnerability Management",
    body: "Dependencies are scanned continuously. Critical CVEs are triaged within 24 hours and remediated on a priority basis. Penetration tests are performed periodically; a summary is available under NDA." },
  { icon: Globe2, title: "Data Residency",
    body: "SUNTRONIX ONE can be provisioned in customer-specific regions. Cross-region data movement follows contractual and regulatory constraints defined in your master services agreement." },
  { icon: ClipboardCheck, title: "Compliance Posture",
    body: "The platform's control architecture is designed to support customer assessments against WCAG 2.2 AA, ISO 27001, SOC 2 and GDPR programmes. We do not claim independent certification unless attested; current attestation letters and Trust reports are available upon request under NDA." },
];

export default function TrustCentre() {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl space-y-8" data-testid="trust-centre">
      <div>
        <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => navigate("/help")}>
          <ArrowLeft className="h-3 w-3" /> Back to Help
        </button>
        <div className="mt-3 text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-500">Trust · Security · Privacy</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1">How SUNTRONIX ONE protects your data</h1>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-2xl">
          Enterprise customers rely on us to steward tens of thousands of devices and the personal data of every employee holding them. This page is a plain-language summary of the controls that make that trust possible. It complements — it does not replace — the contractual security schedule attached to your MSA.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.title} className="bg-white border border-slate-200 rounded-md p-5">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-slate-50 border border-slate-100 grid place-items-center">
                  <Icon className="h-4 w-4 text-slate-700" strokeWidth={1.6} />
                </div>
                <div className="font-heading text-sm font-semibold text-slate-900">{s.title}</div>
              </div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{s.body}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-slate-900 text-slate-200 rounded-md p-6">
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-amber-400">Ask for documentation</div>
        <div className="mt-1 text-lg font-heading text-white">Need our attestation package for your risk review?</div>
        <p className="text-sm text-slate-300 mt-2 leading-relaxed max-w-2xl">
          Enterprise reviews often require our detailed security schedule, sub-processor list, penetration-test summary and data-processing agreement. Contact your Customer Success Manager or e-mail <span className="text-white">trust@suntronix.one</span> to receive the current package under NDA.
        </p>
        <Button className="mt-4 bg-amber-400 text-slate-900 hover:bg-amber-300" onClick={() => window.location.href = "mailto:trust@suntronix.one"} data-testid="trust-request-docs">
          Request documentation <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed">
        Nothing on this page represents a certification or legal representation. Statements above describe the platform's design intent and controls; independent attestation, where relevant, is delivered separately.
      </p>
    </div>
  );
}
